import type {CSSProperties, ReactElement} from 'react';
import {useState} from 'react';
import {getAddedByTitle} from '@Components/base-grid/base-grid.library';
import {isTouchDevice} from '@Utils/browser.util';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import type {UserImageSourceType, UserImageVOResponse} from '@Libraries/user-image-library';
import {BrandKitTag} from '../brand-kit-tag/brand-kit-tag';
import type {GridMediaItemStorage} from '../grid-media-item';
import {GridMediaItem} from '../grid-media-item';
import type {GridItemProps} from '../grid-item';
import {GRID_ITEM_TYPE} from '../grid-item';
import styles from './grid-user-image-item.module.scss';

export interface GridUserImageItemStorage extends GridMediaItemStorage {
  type: GRID_ITEM_TYPE.USER_IMAGE;
  hashedFilename: string;
  extension: string;
  uploaderName: string;
  uploaderId: number;
  source: UserImageSourceType;
  hasTransparency: boolean;
  gridItemWidth?: number;
  isHidden?: boolean;
  isUpscaled?: boolean;
  brandKitName?: string;
  brandKitColors?: string[];
}

interface GridUserImageItemProps extends GridItemProps {
  thumbSrc: string;
  uploaderName: string;
  gridItemWidth?: number;
  isHidden?: boolean;
  brandKitName?: string;
  brandKitColors?: string[];
}

export const getGridUserImageItemStorageForUserImageVO = (userImageVO: UserImageVOResponse): GridUserImageItemStorage => {
  return {
    type: GRID_ITEM_TYPE.USER_IMAGE,
    id: userImageVO.filename,
    hashedFilename: userImageVO.filename,
    extension: userImageVO.ext,
    uploaderName: userImageVO.uploaderName,
    uploaderId: parseInt(userImageVO.uploaderId, 10),
    hasTransparency: userImageVO.hasTransparency,
    source: userImageVO.src,
    isHidden: false,
    width: userImageVO.width,
    height: userImageVO.height,
    isUpscaled: userImageVO.isUpscaled,
    brandKitName: userImageVO.brandKitName,
    brandKitColors: userImageVO.brandKitColors,
  };
};

export function GridUserImageItem({uploaderName, ...props}: GridUserImageItemProps): ReactElement | null {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const isSmallScreen = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);
  if (props.isHidden) {
    return null;
  }

  const inlineStyles: CSSProperties = {...props.style};
  if (props.gridItemWidth) {
    inlineStyles.width = props.gridItemWidth;
  }

  /** Touch input has no hover to reveal the pill, and the uploads modal's mobile layout shows it permanently. */
  const shouldAlwaysShowBrandKitTag = (): boolean => {
    return isSmallScreen || isTouchDevice();
  };

  const getBrandKitTag = (): ReactElement | null => {
    if (!props.brandKitName) {
      return null;
    }
    return (
      <div className={`${styles.brandKitTagReveal} ${shouldAlwaysShowBrandKitTag() ? styles.brandKitTagAlwaysVisible : ''}`}>
        <BrandKitTag brandKitName={props.brandKitName} brandKitColors={props.brandKitColors ?? []} logoThumbSrc={props.thumbSrc} />
      </div>
    );
  };

  return (
    <GridMediaItem
      {...props}
      style={inlineStyles}
      isImageLoaded={isImageLoaded}
      className={props.className ?? ''}
      title={getAddedByTitle(uploaderName)}
      paddedThumb={false}
      thumb={
        <div className={styles.thumbContainer}>
          <img
            className="radius-12"
            onLoad={(): void => {
              setIsImageLoaded(true);
            }}
            src={props.thumbSrc}
            alt={window.i18next.t('pmwjs_image')}
            draggable="false"
            loading="lazy"
          />
          {getBrandKitTag()}
        </div>
      }
    />
  );
}
